import { Link } from 'expo-router';
import { Formik } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { NativeSyntheticEvent, StyleSheet, TextInputChangeEventData, View } from 'react-native';

import { Checkbox, Text as FhsText, tokens } from '@fhs/ui';
import {
  Box,
  FormikFormControl,
  FormikInput,
  InlineAlert,
  Text,
} from '@fhs-legacy/universal-components';
import ActionButton from 'components/action-button';
import { CountrySelect } from 'components/country-select';
import { useLoyaltyContent } from 'hooks/loyalty/use-loyalty-content';
import { useFieldIsRequired } from 'hooks/use-field-is-required';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { useLocale } from 'state/intl';
import {
  POSSIBLE_DEFAULT_OR_EMPTY_DOB_VALUES,
  formatDateObject as dobObjToString,
  dobStringToObj,
  sanitizePhoneNumber,
} from 'utils/form';
import { ISOs as IsoCountryCode, postalCodeInputProperties } from 'utils/form/constants';
import { IDateOfBirth } from 'utils/form/types';
import { validationConfig } from 'utils/form/user-profile-validation-config/validation';
import { useFlag } from 'utils/launchdarkly';

import { FormikBirthdayInput } from '../formik/birthday-input';

import {
  FIREHOUSE_INSIDERS_ARIA_LABEL,
  FIREHOUSE_INSIDERS_INFO_URL,
  FIREHOUSE_INSIDERS_PROGRAM_NAME,
  FIREHOUSE_INSIDERS_TERMS,
  FIREHOUSE_INSIDERS_TERMS_URL,
} from './constants';
import { getFormErrors } from './get-form-errors';
import PromotionalEmailsCheckbox from './promotional-email-checkbox';
import ReloadText from './reload-text';
import { TermsSection } from './terms-section';
import { IUserFormData, IUserInfoSubmitData } from './types';

const InputContainer = Box.withConfig({
  marginBottom: '$3',
});

function NameOfTheDayDisclaimer() {
  const { formatMessage } = useIntl();

  return (
    <InlineAlert
      iconCentered
      status="info"
      backgroundColor="transparent"
      borderColor="blackOpacity.10"
      borderWidth={1}
      marginBottom={6}
      borderRadius={8}
      message={formatMessage(
        { id: 'accountInfoNameDisclaimer' },
        {
          promotionName: (
            <Link href="/nameoftheday" target="_blank">
              <Text underline bold>
                {formatMessage({ id: 'nameOfTheDay' })}
              </Text>
            </Link>
          ),
          support: (
            <Link href="/support" target="_blank">
              <Text underline bold>
                {formatMessage({ id: 'supportReachOut' })}
              </Text>
            </Link>
          ),
          jumpLine: '\n',
        }
      )}
    />
  );
}

interface IUserInfoFormProps {
  readOnly?: boolean;
  user: UserDetails | null;
  /**
   * TODO: this onSubmit requires that a promise be passed
   * because there's a `.then` chain after it's called.
   *
   * We should either move the after submit functionality up to the account-info page
   * or move the submit logic into this form.
   */
  onSubmit: (data: IUserInfoSubmitData) => Promise<any>;
  isLoading?: boolean;
}

function UserInfoForm({ onSubmit, readOnly = false, user, isLoading }: IUserInfoFormProps) {
  const enableNameChangeOnAccountPage = useFlag('enable-name-change-on-account-page');
  const myAccountFieldsVariations = useFlag('my-account-fields-variations');
  const enableNumericPhoneValidation = useFlag('enable-numeric-phone-validation');
  const firehouseInsidersEnabled = useFlag('enable-firehouse-insiders');
  const { formatMessage } = useIntl();
  const { region } = useLocale();

  const { birthdayLegalText } = useLoyaltyContent();

  const DOB_DEFAULT_OR_EMPTY = useMemo(
    () => POSSIBLE_DEFAULT_OR_EMPTY_DOB_VALUES.some(v => v === user?.details?.dob || ''),
    [user]
  );

  const initialValues: IUserFormData = useMemo(() => {
    // dob will come in as string so let's convert it
    const dob = dobStringToObj(user?.details?.dob);
    return {
      agreesToTermsOfService: true,
      dob,
      email: user?.details?.email || '',
      name: user?.details?.name || '',
      phoneNumber: user?.details?.phoneNumber || '',
      promotionalEmails: !!user?.details?.promotionalEmails || false,
      isoCountryCode: (user?.details?.isoCountryCode as IsoCountryCode) || IsoCountryCode.USA,
      zipcode: user?.details?.zipcode || '',
      defaultReloadAmt: user?.details?.defaultReloadAmt || 500,
      defaultReloadPaymentMethodId: user?.details?.defaultPaymentAccountId || '',
      defaultCheckoutPaymentMethodId:
        user?.details?.defaultAccountIdentifier || user?.details?.defaultFdAccountId || '',
      autoReloadEnabled: user?.details?.autoReloadEnabled || false,
      autoReloadThreshold: user?.details?.autoReloadThreshold || 500,
      firehouseSubsInsidersProgramOptIn:
        !!user?.details?.firehouseSubsInsidersProgramOptIn || false,
    };
  }, [user]);

  const [isPendingSubmit, setIsPendingSubmit] = useState(false);

  const handleSubmit = useCallback(
    async (formData: IUserFormData) => {
      setIsPendingSubmit(true);
      try {
        const dob = dobObjToString(formData.dob as IDateOfBirth);

        const phoneNumber = await sanitizePhoneNumber(
          formData.phoneNumber,
          formData.isoCountryCode
        );
        await onSubmit({
          ...formData,
          phoneNumber,
          dob,
        });
      } finally {
        setIsPendingSubmit(false);
      }
    },
    [onSubmit]
  );

  const birthdayIsRequired = useFieldIsRequired(region, validationConfig, 'dob');
  const zipcodeIsRequired = useFieldIsRequired(region, validationConfig, 'zipcode');
  const phoneNumberIsRequired = useFieldIsRequired(region, validationConfig, 'phoneNumber');
  const nameIsReadOnly = !enableNameChangeOnAccountPage;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={getFormErrors({
        formatMessage,
        region,
        myAccountFieldsVariations,
        simplePhoneValidation: enableNumericPhoneValidation,
      })}
      enableReinitialize
    >
      {({
        handleChange,
        handleBlur,
        submitForm,
        values,
        setFieldValue,
        isValid,
        setFieldTouched,
        errors,
        touched,
        isSubmitting,
      }) => {
        const isSubmitInProgress = isSubmitting || isPendingSubmit || isLoading;

        return (
          <>
            <InputContainer>
              {myAccountFieldsVariations.email && (
                <FormikFormControl
                  name="email"
                  label={formatMessage({ id: 'yourEmail' })}
                  isReadOnly
                  testID="email"
                >
                  <FormikInput
                    name="email"
                    inputProps={{
                      isDisabled: true,
                      isRequired: true,
                      testID: 'account-info-email-input',
                      accessibilityLabel: formatMessage({ id: 'yourEmail' }),
                    }}
                  />
                </FormikFormControl>
              )}
              {myAccountFieldsVariations.name && (
                <>
                  <FormikFormControl
                    name="name"
                    label={formatMessage({ id: 'name' })}
                    isReadOnly={nameIsReadOnly}
                    isRequired
                  >
                    <FormikInput
                      name="name"
                      inputProps={{
                        isRequired: true,
                        isDisabled: readOnly || nameIsReadOnly,
                        testID: 'account-info-name-input',
                        accessibilityLabel: formatMessage({ id: 'name' }),
                      }}
                    />
                  </FormikFormControl>
                  {nameIsReadOnly && <NameOfTheDayDisclaimer />}
                </>
              )}
              {myAccountFieldsVariations.phone && (
                <FormikFormControl
                  isRequired={phoneNumberIsRequired}
                  name="phoneNumber"
                  label={formatMessage({ id: 'phoneNumber' })}
                >
                  <FormikInput
                    name="phoneNumber"
                    inputProps={{
                      placeholder: `${formatMessage({ id: 'phoneNumber' })}`,
                      testID: 'signup-phone-input',
                      autoComplete: 'tel',
                      inputMode: 'tel',
                      type: 'tel',
                      isDisabled: readOnly,
                      keyboardType: 'phone-pad',
                      accessibilityLabel: formatMessage({ id: 'phoneNumber' }),
                    }}
                  />
                </FormikFormControl>
              )}
              {myAccountFieldsVariations.dob && (
                <>
                  <FormikFormControl name="dob">
                    <FormikBirthdayInput
                      disabled={readOnly || !DOB_DEFAULT_OR_EMPTY}
                      hintMessage={birthdayLegalText ?? ''}
                      isRequired={birthdayIsRequired}
                      year={values.dob.year}
                      month={values.dob.month}
                      day={values.dob.day}
                      onChange={(inputName, inputText) =>
                        setFieldValue(`dob.${inputName}`, inputText)
                      }
                      testID="account-info-dob"
                      onBlur={inputName => setFieldTouched(`dob.${inputName}`, true)}
                      dobErrors={errors.dob}
                      dobTouched={touched.dob}
                    />
                  </FormikFormControl>
                </>
              )}
              {myAccountFieldsVariations.country && (
                <CountrySelect
                  name="isoCountryCode"
                  label={formatMessage({ id: 'country' })}
                  value={values.isoCountryCode}
                  onChange={handleChange}
                  disabled
                  testID="account-info-country"
                />
              )}
              {myAccountFieldsVariations.zip && (
                <FormikFormControl
                  name="zipcode"
                  isRequired={zipcodeIsRequired}
                  label={formatMessage({
                    id: values.isoCountryCode === IsoCountryCode.USA ? 'zipCode' : 'postalCode',
                  })}
                >
                  <FormikInput
                    name="zipcode"
                    inputProps={{
                      placeholder: formatMessage({
                        id: values.isoCountryCode === IsoCountryCode.USA ? 'zipCode' : 'postalCode',
                      }),
                      isDisabled: readOnly,
                      testID: 'account-info-zip-input',
                      onBlur: () => handleBlur('zipcode'),
                      onChange: (event: NativeSyntheticEvent<TextInputChangeEventData>) => {
                        setFieldValue('zipcode', event.target.toString().toUpperCase());
                      },
                      maxLength: postalCodeInputProperties[values.isoCountryCode]?.maxLength || 10,
                      autoComplete: 'postal-code',
                      accessibilityLabel: formatMessage({
                        id: values.isoCountryCode === IsoCountryCode.USA ? 'zipCode' : 'postalCode',
                      }),
                      ...(values.isoCountryCode === IsoCountryCode.USA && {
                        keyboardType: 'phone-pad',
                      }),
                    }}
                  />
                </FormikFormControl>
              )}
              <ReloadText />
              <PromotionalEmailsCheckbox
                checked={values.promotionalEmails}
                onChange={isSelected => setFieldValue('promotionalEmails', isSelected)}
                disabled={readOnly}
              />
              {firehouseInsidersEnabled && (
                <Checkbox
                  checked={values.firehouseSubsInsidersProgramOptIn}
                  onChange={isSelected =>
                    setFieldValue('firehouseSubsInsidersProgramOptIn', Boolean(isSelected))
                  }
                  label={FIREHOUSE_INSIDERS_ARIA_LABEL}
                  disabled={readOnly}
                >
                  <View style={styles.firehouseInsidersContainer}>
                    <Checkbox.Control />
                    <FhsText.Ui
                      size="md"
                      style={styles.firehouseInsidersLabel}
                      testID="opt-in-label-firehouse-insiders"
                    >
                      I want to join{' '}
                      <Link href={FIREHOUSE_INSIDERS_INFO_URL} asChild target="_blank">
                        <FhsText.Ui size="md" style={styles.firehouseInsidersLink}>
                          {FIREHOUSE_INSIDERS_PROGRAM_NAME}
                        </FhsText.Ui>
                      </Link>
                      {', '}
                      consent to receiving occasional invites to surveys or focus groups via email,
                      and acknowledge that I have read and agree to the{' '}
                      <Link href={FIREHOUSE_INSIDERS_TERMS_URL} asChild target="_blank">
                        <FhsText.Ui size="md" style={styles.firehouseInsidersLink}>
                          {FIREHOUSE_INSIDERS_TERMS}
                        </FhsText.Ui>
                      </Link>
                    </FhsText.Ui>
                  </View>
                </Checkbox>
              )}
            </InputContainer>
            <Box>
              <ActionButton
                testID="account-info-form-submit-button"
                fullWidth
                onPress={submitForm}
                disabled={readOnly || !isValid || isSubmitInProgress}
                isLoading={isSubmitInProgress}
              >
                {formatMessage({ id: 'update' })}
              </ActionButton>
            </Box>
            <TermsSection />
          </>
        );
      }}
    </Formik>
  );
}

const styles = StyleSheet.create({
  firehouseInsidersContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginVertical: 16,
  },
  firehouseInsidersLabel: {
    flex: 1,
  },
  firehouseInsidersLink: {
    color: tokens.colors.$houseRedDarken,
    textDecorationLine: 'underline',
  },
});

export default UserInfoForm;
